import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { TenantViewModel, TenantFeesViewModel, UserViewModel } from '@program-suite/data-common';
import { propagateError } from '@program-suite/util-helpers';
import { AuthStore } from '@program-suite/data-auth';

@Injectable({
  providedIn: 'root',
})
export class AdminSettingsService {
  http = inject(HttpClient);
  authStore = inject(AuthStore);
  controller = '';

  constructor() {
    this.controller = this.authStore.isAdmin() ? 'tenant' : 'Wholesale';
  }

  async getTenantDetails(tenantId?: number): Promise<TenantViewModel> {
    const url = tenantId ? `${this.controller}/tenant-details/${tenantId}` : `${this.controller}/tenant-details`;
    try {
      return await firstValueFrom(this.http.get<TenantViewModel>(url));
    } catch (error) {
      propagateError(error);
      throw new Error('Failed to fetch tenant details. Please try again.');
    }
  }

  async getTenantFees(tenantId?: number): Promise<TenantFeesViewModel> {
    const url = tenantId ? `${this.controller}/tenant-fees/${tenantId}` : `${this.controller}/tenant-fees`;
    try {
      return await firstValueFrom(this.http.get<TenantFeesViewModel>(url));
    } catch (error) {
      propagateError(error);
      throw new Error('Failed to fetch tenant fees. Please try again.');
    }
  }

  async getWholesalers(tenantId?: number): Promise<UserViewModel[]> {
    const url = tenantId ? `${this.controller}/tenant-admins/${tenantId}` : `${this.controller}`;
    try {
      return await firstValueFrom(this.http.get<UserViewModel[]>(url));
    } catch (error) {
      propagateError(error);
      throw new Error('Failed to fetch users. Please try again.');
    }
  }

  async updateTenantDetails(tenant: TenantViewModel): Promise<void> {
    try {
      await firstValueFrom(this.http.post<void>(`${this.controller}/tenant-details`, tenant));
    } catch (error) {
      propagateError(error);
      throw new Error('Failed to update tenant details. Please try again.');
    }
  }

  async updateTenantFees(tenantFees: TenantFeesViewModel): Promise<void> {
    try {
      await firstValueFrom(this.http.post<void>(`${this.controller}/tenant-fees`, tenantFees));
    } catch (error) {
      propagateError(error);
      throw new Error('Failed to update tenant fees. Please try again.');
    }
  }

  async inviteUser(user: UserViewModel): Promise<void> {
    try {
      await firstValueFrom(this.http.post<void>(`${this.controller}/Invite-User`, user));
    } catch (error: any) {
      propagateError(error);
      throw new Error('Failed to invite user. Please try again.');
    }
  }

  async resendInvite(userInviteGuid: string): Promise<void> {
    try {
      await firstValueFrom(this.http.post<void>(`${this.controller}/Resend-Invite/${userInviteGuid}`, null));
    } catch (error: any) {
      propagateError(error);
      throw new Error('Failed to resend invite. Please try again.');
    }
  }

  async cancelInvite(userInviteGuid: string): Promise<void> {
    try {
      await firstValueFrom(this.http.post<void>(`${this.controller}/Cancel-Invite/${userInviteGuid}`, null));
    } catch (error: any) {
      propagateError(error);
      throw new Error('Failed to cancel invite. Please try again.');
    }
  }

  async updateUser(user: UserViewModel): Promise<void> {
    try {
      await firstValueFrom(this.http.post<void>(`${this.controller}/Update-User`, user));
    } catch (error: any) {
      propagateError(error);
      throw new Error('Failed to update user. Please try again.');
    }
  }
}
